import React from 'react';

import {Box} from 'src/design/atoms/Box';
import {List} from 'src/design/atoms/List';
import Parser from 'html-react-parser';
import type {CourseSessionCurrentContentResponse} from 'src/api/ProactApiClient';
import {Subtitle} from 'src/design/atoms/Typography';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {FormViewRow} from 'src/app/forms/FormViewRow/FormViewRow';
import {useGetSkillQuery} from 'src/redux/queries/skill';
import {useAppSelector} from 'src/redux/hooks';
import {fixRichTextTokens} from 'src/shared/utils/imageHandler';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {GetSessionSubtitle} from './sessionHelpers';

export interface TrackerActiveSessionDemonstrationProps {
  contentResponse: CourseSessionCurrentContentResponse;
}

/**
 * Trainee wait screen while a demonstration is pending evaluation. Legacy
 * form path (no skillId) has no Skill sign-off change events wired for it,
 * so it drops the notification promise.
 */
export const TrackerActiveSessionDemonstration: React.FC<
  TrackerActiveSessionDemonstrationProps
> = ({contentResponse}) => {
  const {accessToken} = useAppSelector(state => state.auth);

  const {
    contentDefinition: content,
    contentSession,
    currentContextIndex,
    totalContentLength,
  } = contentResponse;
  const title = GetSessionSubtitle(
    currentContextIndex,
    totalContentLength,
    content?.demonstration?.headerText
  );
  const {
    bodyText: description,
    assignToUser,
    assignToUserGroup,
    skillId,
    skillName,
  } = content!.demonstration!;

  const {data: skill} = useGetSkillQuery(skillId ?? 0, {skip: !skillId});
  const criteria = (skill?.criteria ?? [])
    .slice()
    .sort((a, b) => a.order - b.order);
  const evaluatorName = assignToUser?.name ?? assignToUserGroup?.name;

  return (
    <Box>
      <Subtitle text={title} />
      {description && (
        <FormViewRow
          label="Description"
          value={Parser(fixRichTextTokens(description as string, accessToken))}
        />
      )}
      <Box mt="lg">
        <InfoAlert title="Demonstration in progress">
          {skillId ? (
            <>
              A sign-off request for <strong>{skillName}</strong> has been sent
              to {evaluatorName ?? 'your supervisor'}
              {contentSession?.startedAt
                ? ` (requested ${parseUtcDate(
                    contentSession.startedAt
                  ).toLocaleDateString()})`
                : ''}
              . We'll notify you when it's evaluated. You may safely leave this
              page.
              {criteria.length > 0 && (
                <List withPadding mt="xs">
                  {criteria.map(criterion => (
                    <List.Item
                      key={criterion.id}
                      style={{whiteSpace: 'normal'}}>
                      {criterion.text}
                    </List.Item>
                  ))}
                </List>
              )}
            </>
          ) : (
            <>
              A training demonstration has been assigned to{' '}
              {evaluatorName ?? 'your supervisor'}. You may safely leave this
              page and continue your training once it's completed.
            </>
          )}
        </InfoAlert>
      </Box>
    </Box>
  );
};
